import {
	Conversation,
	InShort,
	Prose,
	RelatedPages,
	type Turn,
} from "@marketing/components/answer";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "What can AI do for a supermarket?",
		a: "An AI shopping assistant lets shoppers order the way they talk: a list in one message, a recipe for a number of people, a budget, the cheapest option. It searches the supermarket's live catalog with each branch's prices and stock, fills the basket and orders only after the shopper confirms.",
	},
	{
		q: "Can it handle a catalog of tens of thousands of products?",
		a: "Yes. Search combines keywords and meaning over the whole catalog, in Arabic script, Arabizi and English, and checks every search's top results against the shopper's message before showing them. The live demo store has about 15,000 products.",
	},
	{
		q: "Does it know our branch prices?",
		a: "Yes. Each branch keeps its own prices and stock, and the shopper sees only what their branch can deliver, in your currency.",
	},
	{
		q: "Can it run a halal catalog?",
		a: "Yes. A retailer can hide alcohol, pork and tobacco, or all three for halal. The server removes them before the assistant sees the catalog, on every channel, and a product not yet checked stays hidden.",
	},
	{
		q: "Does it work for co-operatives with many branches?",
		a: "Yes. Each branch is its own store with its own prices and stock, and on WhatsApp the assistant can ask where the shopper is before showing anything priced, so the basket matches the branch that will deliver.",
	},
	{
		q: "Who handles the conversations it cannot?",
		a: "Your team. Every conversation, typed, voice note or call, is in one live inbox, and a person can take over any chat. Handover to your help desk is possible by integration.",
	},
	{
		q: "Which platforms does it connect to?",
		a: "Shopify, WooCommerce, product feeds including the Google product feed format and JSON lines, our REST API with signed webhooks, and custom connectors for supermarkets with their own platforms.",
	},
];

const SAMPLE: Turn[] = [
	{
		from: "shopper",
		text: "عندكم حليب المراعي؟",
		en: "Do you have Almarai milk?",
	},
	{
		from: "assistant",
		text: "أبشر، موجود. تحب كامل الدسم ولا خالي الدسم؟",
		en: "Sure, we have it. Would you like full fat or fat free?",
		note: "Showed 8 milk cards, Almarai full fat and fat free 1 L first, then other brands with their offers.",
	},
	{ from: "shopper", text: "what is your cheapest rice?" },
	{
		from: "assistant",
		text: "Our cheapest rice is Min Dayaa Egyptian Rice 900 g at $1.05. Would you like me to add it to your basket?",
		note: "Showed 8 rice cards from the lowest price up.",
	},
];

const NEEDS = [
	{
		title: "Big catalogs",
		text: "Keyword and meaning search over every department, checked against the shopper's message. It never assumes food.",
	},
	{
		title: "Pack sizes",
		text: "1 L or 2 L, 6 eggs or 15: recipes and lists pick the pack that covers what is needed for the least money.",
	},
	{
		title: "Branch prices",
		text: "Each branch's own prices and stock, so the basket is the one that will be delivered.",
	},
	{
		title: "Recipes",
		text: '"Kabsa for 10" becomes ingredient lines with amounts, the right packs and one tap to add them all.',
	},
	{
		title: "Budgets and cheapest",
		text: '"Under 50 riyals", "cheapest first" and "more options" are understood and kept for follow-ups.',
	},
	{
		title: "Halal by the server",
		text: "Alcohol, pork and tobacco removed before the assistant sees the catalog, in every channel.",
	},
];

const LIVE = [
	{
		title: "Connect your catalog",
		text: "With every branch's prices and stock, from your platform, a feed or our API.",
	},
	{
		title: "Set your rules and test",
		text: "Dialect, products you never sell, handover. Then tests generated from your own catalog.",
	},
	{
		title: "Open a channel",
		text: "Your app, your WhatsApp Business number or phone orders, one at a time or together.",
	},
];

export const Route = createFileRoute("/_marketing/supermarkets")({
	head: () =>
		marketingHead({
			title: "AI for Supermarkets and Grocery Retailers | Hephon Commerce",
			description:
				"AI for supermarkets: a shopping assistant that searches your whole catalog with branch prices, handles recipes and budgets, and enforces halal rules.",
			path: "/supermarkets",
			breadcrumb: ["Supermarkets"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: SupermarketsPage,
});

function SupermarketsPage() {
	return (
		<>
			<PageIntro
				kicker="Supermarkets"
				title="AI for supermarkets that shops your whole catalog, branch by branch"
				lead="Shoppers ask in their own words. The assistant finds the products, picks the packs, keeps to the branch's prices and fills the basket."
			/>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce is an AI shopping assistant for supermarkets
					and grocery retailers. Shoppers ask for products, lists,
					recipes or the cheapest option in Arabic, Arabizi or
					English, and it searches the live catalog with each branch's
					prices and stock and fills the basket.
				</p>
				<p>
					It works in the supermarket's own app and website, on its
					WhatsApp Business number and on phone calls, and nothing is
					ordered until the shopper confirms.
				</p>
			</InShort>

			<Section
				id="needs-title"
				title="What does grocery AI need to get right in a supermarket?"
				lead="A supermarket is not a fashion store with a chat window. These are the details that decide whether a basket is right."
			>
				<DetailGrid items={NEEDS} />
			</Section>

			<Section
				id="catalog-title"
				tone="mist"
				title="Can it find the right product in a big catalog?"
			>
				<Prose>
					<p>
						A supermarket catalog has tens of thousands of products,
						many with near-identical names, and shoppers describe
						them in every way: "7aleeb" in Arabizi, حليب in Arabic,
						"milk" in English, a brand name, a misspelling. Hephon
						Commerce searches by keyword and by meaning at once,
						over Arabic script, Arabizi and English, so "tomato"
						finds "Tomatoes" and a brand written in Arabic finds the
						product listed in English.
					</p>
					<p>
						Search alone is not enough, because it always returns
						something. Before results are shown, a fast classifier
						checks each of the top results against what the shopper
						actually wrote and drops the ones that do not fit.
						Snacks for a basketball night show snacks, not
						basketballs. The assistant also never assumes a shopper
						wants food: if your store sells phone chargers, a
						request for a charger gets chargers.
					</p>
					<p>
						A list in one message, such as "milk, bread and eggs",
						comes back as a row of choices per item. "More options"
						leaves out what the shopper has already seen, and
						"something similar" stays in the same category.
					</p>
				</Prose>
			</Section>

			<Section
				id="sample-title"
				title="What does a conversation look like?"
				lead="Two requests to the live demo store, each in a new chat."
			>
				<Conversation
					turns={SAMPLE}
					caption="Captured from the live demo store, Hephon Market, on 5 November 2026; prices in US dollars from the demo catalog. The two requests were made in separate chats and are shown together here."
				/>
			</Section>

			<Section
				id="branch-title"
				tone="mist"
				title="How does it handle branch prices, pack sizes and budgets?"
			>
				<Prose>
					<p>
						Every branch is its own store in Hephon Commerce, with
						its own prices and stock. The shopper only sees what
						their branch can deliver, at that branch's price, in
						your currency. On WhatsApp the assistant can ask where
						the shopper is before it shows anything priced, and a
						typed area is placed at the nearest branch.
					</p>
					<p>
						Pack sizes matter most in recipes and lists. A dish for
						ten people becomes ingredient lines with amounts in
						grams, millilitres or pieces, and for each line the
						assistant picks the pack that covers the amount for the
						least money and how many to buy. Staples the shopper
						probably has are listed but left out of "Add all". See{" "}
						<a href="/shoppable-recipes">shoppable recipes</a> for
						real baskets from the demo store.
					</p>
					<p>
						Budgets are part of the search, not a filter applied
						afterwards. A price limit per product ("rice under 20
						riyals") and one budget for a whole list ("snacks for
						the school week under 50 riyals") are both understood,
						and both hold for follow-up questions. "Cheapest" shows
						what passed the relevance check from the lowest price
						up.
					</p>
				</Prose>
			</Section>

			<Section
				id="rules-title"
				title="Halal rules, co-operatives and your team"
			>
				<Prose>
					<h3>Halal on the server</h3>
					<p>
						A supermarket can hide alcohol, pork and tobacco, or all
						three for a halal catalog. Each product is judged by an
						AI model rather than a word list, and the server removes
						what the store never sells before the assistant sees the
						catalog, on every channel. A product that has not been
						checked yet stays hidden. A recipe in a halal store
						never includes those ingredients.
					</p>
					<h3>Co-operatives and multi-branch chains</h3>
					<p>
						Co-operative societies and regional chains often run
						many branches with different ranges and prices. Each
						branch is set up as its own store, so a shopper in one
						district never gets a basket priced for another. If a
						shopper moves to another branch on WhatsApp, the basket
						follows, and for items the new branch does not sell the
						assistant offers substitutes from that branch.
					</p>
					<h3>One inbox for the team</h3>
					<p>
						Every conversation, typed, voice note or call with a
						transcript, lands in one live inbox. A person can take
						over any chat, and a shopper can ask for a person at any
						time. Handover to your help desk is possible by
						integration.
					</p>
				</Prose>
			</Section>

			<Section
				id="fit-title"
				tone="mist"
				title="When Hephon Commerce is not the right fit"
			>
				<Prose>
					<ul>
						<li>
							<strong>No online catalog yet.</strong> The
							assistant shops your live products, prices and
							stock. A supermarket without them should start
							there.
						</li>
						<li>
							<strong>In-store technology.</strong> It is not
							shelf monitoring, pricing optimisation or demand
							forecasting. It is the conversation with the
							shopper.
						</li>
						<li>
							<strong>Support-only needs.</strong> If you mostly
							need answers about deliveries and refunds, a support
							tool fits better.
						</li>
					</ul>
				</Prose>
			</Section>

			<Section id="live-title" title="Live in three steps." center>
				<Steps items={LIVE} />
			</Section>

			<Faq items={FAQ} title="Supermarkets ask." />
			<RelatedPages
				links={[
					{ href: "/quick-commerce", label: "AI for quick commerce" },
					{ href: "/shoppable-recipes", label: "Shoppable recipes" },
					{
						href: "/ai-agent-for-ecommerce",
						label: "Ecommerce AI agent",
					},
					{ href: "/saudi-arabia", label: "Saudi Arabia" },
					{ href: "/uae", label: "UAE" },
					{ href: "/integrations", label: "Integrations" },
				]}
			/>
			<Closing />
		</>
	);
}
